import { mutate } from 'swr';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { apiUrl } from '@dashboard/services/env';
import logException from '@dashboard/services/logException';
import request from '@dashboard/services/request';

export const signFile = async (token, values) => {
  try {
    const key = '/files/sign';

    const response = await request(apiUrl + key, token, {
      body: JSON.stringify(values),
      method: 'POST',
    });

    if (!response.data) {
      const error = new Error(response.message || response.error);
      error.response = response;
      throw error;
    }

    return { data: response.data, error: null };
  } catch (error) {
    logException({ message: 'Failed to sign file', error });
    if (error && error.message) {
      return { error: error.message };
    }

    return { error: 'Unable to sign file', data: null };
  }
};

export const uploadFileS3 = async (signedUrl, file, onProgress) => {
  try {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded * 100) / event.total);
          onProgress?.(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve({ success: true });
        } else {
          reject(new Error('Upload failed'));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  } catch (error) {
    logException({ message: 'Failed to upload file', error });
    throw error?.message || 'Failed to upload file';
  }
};

export const createFile = async (token, fileInfo) => {
  try {
    const key = `/files`;

    const response = await request(apiUrl + key, token, {
      method: 'POST',
      body: JSON.stringify(fileInfo),
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    mutate(`/files/${response.data._id}`, response.data);

    return { data: response.data, error: null };
  } catch (error) {
    logException({ message: 'Failed to save file', error });
    throw error?.message || 'Failed to upload file';
  }
};

export const uploadFile = async ({ file, bucket, sellerId, onProgress }, token) => {
  const { data, error } = await signFile(token, {
    name: file.name,
    type: file.type,
    bucket: bucket,
  });

  if (error) {
    throw new Error('Failed to sign');
  }

  const { signedUrl, location, filekey } = data;

  const uploaded = await uploadFileS3(signedUrl, file, onProgress);

  if (!uploaded) {
    throw new Error('Failed to upload to S3');
  }

  return createFile(token, {
    seller: sellerId,
    location,
    bucket,
    name: file.name,
    filekey,
    lastModified: file.lastModified,
    size: file.size,
    type: file.type,
    width: file.width,
    height: file.height,
  });
};

export const updateFile = async (token, values, fileId) => {
  try {
    const key = `/files/${fileId}`;
    const response = await request(apiUrl + key, token, {
      method: 'PUT',
      body: JSON.stringify(values),
    });

    if (!response) {
      throw new Error('Failed to edit file');
    }

    mutate(key, response);

    return { data: response.data, error: null };
  } catch (error) {
    logException({ message: 'Failed to edit file', error });

    throw error?.message || 'Failed to edit file';
  }
};

export const deleteFile = async (token, fileId) => {
  try {
    const key = `/files/${fileId}`;

    const response = await request(apiUrl + key, token, {
      method: 'DELETE',
    });

    if (!response) {
      throw new Error('Failed to delete file');
    }

    // 409 means another page or product still uses the file. It stays in
    // storage so those keep working; the caller has already detached its own copy.
    if (response.meta?.status === 409) {
      return false;
    }

    if (response.meta && !response.meta.ok) {
      const error = new Error(response.message || 'Failed to delete file');
      error.response = response;
      throw error;
    }

    mutate(key, null);

    return true;
  } catch (error) {
    logException({ message: 'Failed to delete file', error });

    throw error?.message || 'Failed to delete file';
  }
};

export const downloadFile = async (fileId, token) => {
  try {
    const key = `/files/${fileId}/download`;
    const response = await request(apiUrl + key, token, {
      method: 'GET',
    });

    if (!response.data) {
      const error = new Error(response.message);
      error.response = response;
      throw error;
    }

    return { data: response.data, error: null };
  } catch (error) {
    logException({ message: 'Failed to download file', error });

    throw error?.message || 'Failed to download file';
  }
};

export default function useFileQueries() {
  const { token } = useAuth();

  return {
    uploadFile: ({ file, bucket = 'images', sellerId, onProgress }) =>
      uploadFile({ file, bucket, sellerId, onProgress }, token),
    signFile: values => signFile(token, values),
    uploadFileS3: (signedUrl, file) => uploadFileS3(signedUrl, file),
    createFile: (fileInfo, fileId) => createFile(token, fileInfo, fileId),
    updateFile: (fileId, values) => updateFile(token, values, fileId),
    deleteFile: fileId => deleteFile(token, fileId),
    downloadFile: fileId => downloadFile(fileId, token),
  };
}
